/**
 * ValidateContext — shared state and actions for the validate feature.
 *
 * Two separate contexts prevent cross-cutting re-renders:
 *   - ValidateStateContext   → re-renders when any state field changes
 *   - ValidateActionsContext → stable; callbacks don't change between renders
 *
 * Hooks:
 *   useValidateState()            — read state only
 *   useValidateActions()          — stable action refs only
 *   useValidateContext()          — merged (use when you need both)
 *   useValidateContextOptional()  — returns null outside the provider (Storybook / tests)
 */

import { createContext, useMemo, useContext, type ReactNode } from 'react';
import { useValidateDetails } from '../hooks/useValidateDetails';
import type { ValidateState, ValidateActions, ValidateContextValue } from './validateContext.types';

const ValidateStateContext   = createContext<ValidateState   | null>(null);
const ValidateActionsContext = createContext<ValidateActions | null>(null);

export type ValidateProviderProps = { children: ReactNode };

// Selector functions keep the useMemo deps arrays explicit and type-safe.
function selectState(v: ValidateContextValue): ValidateState {
  return {
    mission_id:                  v.mission_id,
    mission_name:                v.mission_name,
    satelliteOptions:            v.satelliteOptions,
    selectedSatellite:           v.selectedSatellite,
    loadingSatellites:           v.loadingSatellites,
    filteredSetups:              v.filteredSetups,
    activeSetup:                 v.activeSetup,
    loadingSetups:               v.loadingSetups,
    loadingSessions:             v.loadingSessions,
    filteredSessions:            v.filteredSessions,
    sessionsForActiveSetup:      v.sessionsForActiveSetup,
    sessionsForSatellite:        v.sessionsForSatellite,
    totalDurationMinutes:        v.totalDurationMinutes,
    hasRunningSession:           v.hasRunningSession,
    sessionFilterList:           v.sessionFilterList,
    allFilters:                  v.allFilters,
    isCreateModalOpen:           v.isCreateModalOpen,
    groundStationOptions:        v.groundStationOptions,
    loadingProviders:            v.loadingProviders,
    modelTypeOptions:            v.modelTypeOptions,
    loadingSetupConfig:          v.loadingSetupConfig,
    isCreateSessionModalOpen:    v.isCreateSessionModalOpen,
    testingEnvironmentOptions:   v.testingEnvironmentOptions,
    loadingSessionConfig:        v.loadingSessionConfig,
    deleteTarget:                v.deleteTarget,
  };
}

function selectActions(v: ValidateContextValue): ValidateActions {
  return {
    onSatelliteChange:       v.onSatelliteChange,
    onSetupSelect:           v.onSetupSelect,
    setSetups:               v.setSetups,
    setAllFilters:           v.setAllFilters,
    openCreateModal:         v.openCreateModal,
    closeCreateModal:        v.closeCreateModal,
    openCreateSessionModal:  v.openCreateSessionModal,
    closeCreateSessionModal: v.closeCreateSessionModal,
    onDeleteOpen:            v.onDeleteOpen,
    onEndSessionOpen:        v.onEndSessionOpen,
    onSetupDeleteOpen:       v.onSetupDeleteOpen,
    onDeleteConfirm:         v.onDeleteConfirm,
    onDeleteCancel:          v.onDeleteCancel,
    onSearchFilterApply:     v.onSearchFilterApply,
    onStartNewSession:       v.onStartNewSession,
    onOpenRunningSession:    v.onOpenRunningSession,
    startDeployPolling:      v.startDeployPolling,
    addPendingSession:       v.addPendingSession,
    refreshSessionsForSatellite: v.refreshSessionsForSatellite,
    refreshSetupsForSatellite:   v.refreshSetupsForSatellite,
  };
}

export function ValidateProvider({ children }: ValidateProviderProps) {
  const full = useValidateDetails();

  const state = useMemo(
    () => selectState(full),
    // Each dep is a primitive or stable useCallback/useMemo ref —
    // the memo only invalidates when something actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      full.mission_id, full.mission_name,
      full.satelliteOptions, full.selectedSatellite, full.loadingSatellites,
      full.filteredSetups, full.activeSetup, full.loadingSetups, full.loadingSessions,
      full.filteredSessions, full.sessionsForActiveSetup, full.sessionsForSatellite,
      full.totalDurationMinutes, full.hasRunningSession,
      full.sessionFilterList,
      full.allFilters,
      full.isCreateModalOpen, full.groundStationOptions, full.loadingProviders,
      full.modelTypeOptions, full.loadingSetupConfig,
      full.isCreateSessionModalOpen, full.testingEnvironmentOptions, full.loadingSessionConfig,
      full.deleteTarget,
    ],
  );

  const actions = useMemo(
    () => selectActions(full),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      full.onSatelliteChange, full.onSetupSelect, full.setSetups, full.setAllFilters,
      full.openCreateModal, full.closeCreateModal,
      full.openCreateSessionModal, full.closeCreateSessionModal,
      full.onDeleteOpen, full.onEndSessionOpen, full.onSetupDeleteOpen, full.onDeleteConfirm, full.onDeleteCancel,
      full.onSearchFilterApply, full.onStartNewSession, full.onOpenRunningSession,
      full.startDeployPolling, full.addPendingSession, full.refreshSessionsForSatellite, full.refreshSetupsForSatellite,
    ],
  );

  return (
    <ValidateStateContext.Provider value={state}>
      <ValidateActionsContext.Provider value={actions}>
        {children}
      </ValidateActionsContext.Provider>
    </ValidateStateContext.Provider>
  );
}

export function useValidateState(): ValidateState {
  const ctx = useContext(ValidateStateContext);
  if (ctx == null) throw new Error('useValidateState must be inside ValidateProvider');
  return ctx;
}

export function useValidateActions(): ValidateActions {
  const ctx = useContext(ValidateActionsContext);
  if (ctx == null) throw new Error('useValidateActions must be inside ValidateProvider');
  return ctx;
}

/** Merged state + actions. Use when you need both. */
export function useValidateContext(): ValidateContextValue {
  const state   = useValidateState();
  const actions = useValidateActions();
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}
